<template>
  <div class="file-upload-widget " :class="{ compact: compact }">
    <!-- COMPACT MODE -->
    <div v-if="compact" class="compact-view">
      <FileUploader
        :acceptedFilesTypes="acceptedFilesTypes"
        @onFileChangedV2="(data) => newFileSelected(data)"
        @uploadFailed="() => uploadFailed()"
        @uploading="(data) => onUploading(data)"
        :key="filePikerKey"
        :disabled="files.length >= maxFiles"
        :maxFileSize="maxFileSize"
        exactTypeMatch
        :makeLoadingModal="false"
      >
        <div
          @click="handleClick"
          class="w-full flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-primary-400 transition-all"
        >
          <div v-if="!files[0]" class="flex items-center gap-3 w-full">
            <div
              class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0"
            >
              <ImgComponent
                src="/images/all/image.png"
                alt=""
                class="w-5 h-5"
              />
            </div>
            <span class="text-sm text-gray-500 flex-1"
              >Choisir une image...</span
            >
            <span class="text-xs text-primary-600 font-medium">Parcourir</span>
          </div>
          <div v-else class="flex items-center gap-3 w-full">
            <div
              class="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"
            >
              <img
                :src="files[0].url"
                alt=""
                class="w-full h-full object-cover"
              />
            </div>
            <span class="text-sm text-gray-700 flex-1 truncate">{{
              files[0].name
            }}</span>
            <span
              class="text-xs text-red-500 font-medium cursor-pointer hover:text-red-600"
              @click.stop="removePicture(0)"
            >
              Supprimer
            </span>
          </div>
        </div>
      </FileUploader>
    </div>

    <!-- FULL MODE -->
    <div v-else class="full-view">
      <!-- DROP ZONE -->
      <div class="mb-4 w-full">
        <FileUploader
          :acceptedFilesTypes="acceptedFilesTypes"
          @onFileChangedV2="(data) => newFileSelected(data)"
          @uploadFailed="() => uploadFailed()"
          @uploading="(data) => onUploading(data)"
          :key="filePikerKey"
          :disabled="files.length >= maxFiles"
          :maxFileSize="maxFileSize"
          exactTypeMatch
          :makeLoadingModal="false"
        >
          <div
            @click="handleClick"
            class="cursor-pointer w-full flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 rounded-2xl hover:border-primary-400 transition-all bg-gray-50"
          >
            <div class="w-16 h-16 mb-4">
              <ImgComponent :src="imgPicture" alt="" class="w-full h-full" />
            </div>
            <p class="text-gray-700 text-center font-medium">
              Glisser-déposer, ou
              <span class="text-primary-600 underline cursor-pointer"
                >Parcourir</span
              >
            </p>
            <p class="text-gray-500 text-sm mt-2 text-center">
              jpeg, jpg, png ou webp
            </p>
          </div>
        </FileUploader>
      </div>

      <!-- FILE LIST -->
      <div v-if="files && files[0]" class="space-y-3">
        <div class="text-sm font-medium text-gray-700">Image téléchargée</div>
        <div
          v-for="(file, i) in files"
          :key="i"
          class="relative flex items-center gap-4 p-3 bg-white rounded-xl border border-gray-200"
          :class="{ 'opacity-50': file.isUploading }"
        >
          <!-- FILE THUMBNAIL -->
          <div
            class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100"
          >
            <img :src="file.url" alt="" class="w-full h-full object-cover" />
          </div>

          <!-- FILE INFO -->
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate">
              {{ file.name }}
            </div>
            <div class="text-xs text-gray-500">
              {{ file.type.replace(/.+\//, '') }} &bull; {{ file.size }}
            </div>
          </div>

          <!-- PROGRESS BAR -->
          <div
            v-if="file.isUploading"
            class="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-xl overflow-hidden"
          >
            <div
              class="h-full bg-primary-600 transition-all duration-300"
              :style="{ width: file.progress + '%' }"
            ></div>
          </div>

          <!-- DELETE BUTTON -->
          <button
            class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
            @click="removePicture(i, file)"
          >
            <ColorImg :src="trashImg" alt="" class="w-8 h-8 bg-current" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { getFileNameFromUrl, getMimeTypeFromUrl } from './fileUtils';
import imgPicture from './images/image.png';
import trashImg from './images/close.svg';

export default defineComponent({
  name: 'FileUploadWidget',

  props: {
    compact: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: Number,
      default: 1
    },
    minFiles: {
      type: Number,
      default: 1
    },
    acceptedFiles: {
      type: Array,
      default() {
        return ['image'];
      }
    },
    defaultUrls: {
      type: Array,
      default() {
        return [];
      }
    },
    modelValue: {
      type: String,
      default: ''
    },
    maxFileSize: {
      type: [Number, String],
      default: null
    }
  },

  emits: ['update:modelValue', 'change'],

  setup() {
    return { imgPicture, trashImg };
  },

  data() {
    return {
      filePikerKey: 'none',
      files: this.defaultUrls.map((url) => ({
        id: url,
        name: getFileNameFromUrl(url),
        url,
        type: getMimeTypeFromUrl(url),
        size: ''
      })),
      fileTypes: {
        png: 'image/png',
        jpg: 'image/jpg',
        jpeg: 'image/jpeg',
        webp: 'image/webp'
      }
    };
  },

  methods: {
    handleClick(event) {
      if (this.files.length >= this.maxFiles) {
        event.stopPropagation();
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Limite atteinte',
          body: `Vous ne pouvez pas ajouter plus de ${this.maxFiles} image${
            this.maxFiles > 1 ? 's' : ''
          }. Supprimez une image pour continuer.`
        });
      }
    },

    newFileSelected({ id, name, url, type, size }) {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.files[this.files.length - 1] = { id, name, url, type, size };
      } else {
        this.files.push({ id, name, url, type, size });
      }
      this.filePikerKey = `${Date.now()}`;
      this.emitUpdate();
    },

    uploadFailed() {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.removePicture(this.files.length - 1);
      }
    },

    onUploading(data) {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.files[this.files.length - 1] = { ...data, isUploading: true };
      } else {
        this.files.push({ ...data, isUploading: true });
      }
    },

    removePicture(index, pic) {
      if (pic?.isUploading) {
        this.filePikerKey = `${Date.now()}`;
      }
      this.files = this.files.filter((file, i) => i !== index);
      this.emitUpdate();
    },

    emitUpdate() {
      const url = this.files[0]?.url || '';
      this.$emit('update:modelValue', url);
      this.$emit('change', url);
    },

    getData() {
      return this.files;
    }
  },

  computed: {
    acceptedFilesTypes() {
      const res = [];
      this.acceptedFiles.map((accepted) => {
        if (/image/i.test(accepted)) {
          res.push(
            this.fileTypes.png,
            this.fileTypes.jpg,
            this.fileTypes.jpeg,
            this.fileTypes.webp
          );
        } else if (/png/i.test(accepted)) res.push(this.fileTypes.png);
        else if (/jpg/i.test(accepted)) res.push(this.fileTypes.jpg);
        else if (/jpeg/i.test(accepted)) res.push(this.fileTypes.jpeg);
        else if (/webp/i.test(accepted)) res.push(this.fileTypes.webp);
      });
      return res;
    }
  }
});
</script>
